import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authLogin, authRegister, authMe } from '@/lib/api';
import { getToken, setToken, clearToken } from '@/lib/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};

function toProfile(user) {
  if (!user) return null;
  return {
    id: user.id,
    email: user.email,
    role: user.role,
    full_name: user.name || '',
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    access_scope: Array.isArray(user.access_scope) ? user.access_scope : undefined,
    is_super_admin: user.is_super_admin === true,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyUser = useCallback((nextUser) => {
    setUser(nextUser || null);
    setProfile(toProfile(nextUser));
  }, []);

  const bootstrap = useCallback(async () => {
    const token = getToken();
    if (!token) {
      applyUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await authMe();
      applyUser(data.user);
    } catch (err) {
      clearToken();
      applyUser(null);
    } finally {
      setLoading(false);
    }
  }, [applyUser]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const signIn = async ({ email, password }) => {
    const { data } = await authLogin({ email: email.trim().toLowerCase(), password });
    setToken(data.access_token);
    applyUser(data.user);
    return { user: data.user };
  };

  const signUpStudent = async ({ email, password, fullName }) => {
    const { data } = await authRegister({
      email: email.trim().toLowerCase(),
      password,
      name: fullName,
    });
    setToken(data.access_token);
    applyUser(data.user);
    return { user: data.user };
  };

  const signOut = async () => {
    clearToken();
    applyUser(null);
  };

  const refreshProfile = async () => {
    try {
      const { data } = await authMe();
      applyUser(data.user);
      return toProfile(data.user);
    } catch (err) {
      clearToken();
      applyUser(null);
      return null;
    }
  };

  const value = {
    user,
    profile,
    role: profile?.role || null,
    loading,
    signIn,
    signUpStudent,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
